import { useRef } from 'react';
import { useMutation } from 'react-query';

import { Config } from '@nw/shared/config';
import {
    getAPIErrorMessage,
    getSessionStorageToken,
    setSessionStorageToken
} from '@nw/shared/utils';

export type InstituteApiVersion = 'v2' | 'v3';

const getInstituteUrl = (instituteApiVersion: InstituteApiVersion): string => {
    const baseUrl = `${Config['COMMON_DATA_INSTITUTE_BASE_URL']}`;
    if (instituteApiVersion === 'v3') {
        return baseUrl.replace('/v2/', '/v3/');
    }
    return baseUrl;
};

const fetchInstituteData = (
    signal: AbortSignal,
    searchValue: string,
    instituteApiVersion: InstituteApiVersion
) => {
    return fetch(getInstituteUrl(instituteApiVersion), {
        signal,
        method: 'POST',
        body: JSON.stringify({
            data: JSON.stringify(
                JSON.stringify({
                    search_value: searchValue,
                    offset: 0,
                    limit: 100
                })
            ),
            clientKeyDetailsId: 1
        }),
        headers: { 'content-type': 'application/json' }
    }).then((res) => res.json());
};

export const useGetInstitutesMutation = (
    instituteApiVersion: InstituteApiVersion
) => {
    const activeRequests = useRef<AbortController[]>([]); // Ref to store active AbortControllers

    const getInstitutes = async (params: { filterText: string }) => {
        // Cancel all previous requests
        activeRequests.current.forEach((controller) => controller.abort());
        activeRequests.current = [];

        const abortController = new AbortController();
        activeRequests.current.push(abortController);

        try {
            const textToSearch = params.filterText.trim();
            const key: string = textToSearch.toLocaleLowerCase();
            const sessionStorageKey = `institutesName-${instituteApiVersion}-${key}`;

            const storedResponse = getSessionStorageToken(sessionStorageKey);
            if (storedResponse) return JSON.parse(storedResponse);

            const signal = abortController.signal;
            const data = await fetchInstituteData(
                signal,
                textToSearch,
                instituteApiVersion
            );
            setSessionStorageToken(sessionStorageKey, data);

            return data;
        } catch (error) {
            if (
                error instanceof Error &&
                (error.name === 'AbortError' || error.name === 'CanceledError')
            ) {
                throw error;
            }
            // Error handling - return empty result on error
            return { institutes: [] };
        }
    };

    const { mutateAsync, error, status } = useMutation(getInstitutes);

    const getInstitutesApi = (params: { filterText: string }) => {
        return mutateAsync(params);
    };

    return {
        getInstitutesApi,
        error: error ? getAPIErrorMessage(error) : '',
        status
    };
};
